import type { ExcalidrawElement } from "@excalidraw/element/types";

/**
 * Lock state management utilities for enhanced lock system
 */

export interface LockState {
  lockedElementIds: Set<string>;
  bulkLockActive: boolean;
  showLockIndicators: boolean;
}

export interface LockActions {
  toggleElementLock: (elementIds: string[]) => void;
  lockAllElements: () => void;
  unlockAllElements: () => void;
  setLockIndicatorVisibility: (visible: boolean) => void;
}

/**
 * Check if all elements in the array are locked
 */
export const areAllElementsLocked = (
  elements: readonly ExcalidrawElement[],
): boolean => {
  return elements.length > 0 && elements.every((el) => el.locked);
};

/**
 * Check if any elements in the array are locked
 */
export const areAnyElementsLocked = (
  elements: readonly ExcalidrawElement[],
): boolean => {
  return elements.some((el) => el.locked);
};

/**
 * Check if all elements in the array are unlocked
 */
export const areAllElementsUnlocked = (
  elements: readonly ExcalidrawElement[],
): boolean => {
  return elements.length > 0 && elements.every((el) => !el.locked);
};

/**
 * Get all locked elements from the scene
 */
export const getLockedElements = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawElement[] => {
  return elements.filter((el) => el.locked);
};

/**
 * Get all unlocked elements from the scene
 */
export const getUnlockedElements = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawElement[] => {
  return elements.filter((el) => !el.locked);
};

/**
 * Check if bulk lock operations should be available
 * Returns object indicating which bulk operations are available
 */
export const getBulkLockAvailability = (
  elements: readonly ExcalidrawElement[],
  appState: { selectedElementIds: Record<string, boolean> },
): {
  canLockAll: boolean;
  canUnlockAll: boolean;
  hasLockedElements: boolean;
  hasUnlockedElements: boolean;
} => {
  const selectedElements = elements.filter(
    (el) => appState.selectedElementIds[el.id],
  );
  const hasSelection = selectedElements.length > 0;

  if (hasSelection) {
    // When elements are selected, check selected elements
    const hasLockedSelected = areAnyElementsLocked(selectedElements);
    const hasUnlockedSelected = selectedElements.some((el) => !el.locked);

    return {
      canLockAll: hasUnlockedSelected,
      canUnlockAll: hasLockedSelected,
      hasLockedElements: hasLockedSelected,
      hasUnlockedElements: hasUnlockedSelected,
    };
  }
  // When no elements are selected, check all elements
  const lockedElements = getLockedElements(elements);
  const unlockedElements = getUnlockedElements(elements);

  return {
    canLockAll: unlockedElements.length > 0,
    canUnlockAll: lockedElements.length > 0,
    hasLockedElements: lockedElements.length > 0,
    hasUnlockedElements: unlockedElements.length > 0,
  };
};

/**
 * Determine the target elements for bulk lock operations
 */
export const getBulkLockTargetElements = (
  elements: readonly ExcalidrawElement[],
  appState: { selectedElementIds: Record<string, boolean> },
  operation: "lock" | "unlock",
): ExcalidrawElement[] => {
  const selectedElements = elements.filter(
    (el) => appState.selectedElementIds[el.id],
  );
  const hasSelection = selectedElements.length > 0;

  if (hasSelection) {
    // When elements are selected, target selected elements
    if (operation === "lock") {
      return selectedElements.filter((el) => !el.locked);
    }
    return selectedElements.filter((el) => el.locked);
  }
  // When no elements are selected, target all elements
  if (operation === "lock") {
    return getUnlockedElements(elements);
  }
  return getLockedElements(elements);
};

/**
 * Check if lock indicators should be shown for an element
 */
export const shouldShowLockIndicator = (
  element: ExcalidrawElement,
): boolean => {
  return element.locked && !element.isDeleted;
};

/**
 * Get the lock indicator position for an element
 */
export const getLockIndicatorPosition = (
  element: ExcalidrawElement,
  zoom: number,
): { x: number; y: number; size: number } => {
  // Ensure zoom is within reasonable bounds to prevent positioning issues
  const clampedZoom = Math.max(0.1, Math.min(5.0, zoom));

  // Calculate indicator size with reasonable bounds, but ensure it fits within element
  const baseSize = 16;
  const maxSize = Math.min(32, element.width * 0.3, element.height * 0.3);
  const indicatorSize = Math.max(8, Math.min(maxSize, baseSize / clampedZoom));

  // Calculate padding with minimum value to prevent negative positioning
  const basePadding = 2;
  const padding = Math.max(1, basePadding / clampedZoom);

  // Position indicator at top-right corner, ensuring it stays within element bounds
  const x = Math.max(
    element.x,
    element.x + element.width - indicatorSize - padding,
  );
  const y = Math.max(element.y, element.y + padding);

  // Final bounds check to ensure indicator doesn't exceed element boundaries
  const finalX = Math.min(x, element.x + element.width - indicatorSize);
  const finalY = Math.min(y, element.y + element.height - indicatorSize);

  return {
    x: Math.max(element.x, finalX),
    y: Math.max(element.y, finalY),
    size: indicatorSize,
  };
};
